import { canv } from "@/canv/global";
import { drawRoundedRect, isOverRect } from "@/canv/utils";
import type { TableWidget } from "./table";
import type {
    ActionsInfoResult,
    AddBarAction,
    AddBarActionsInfoResult,
    BuildAction,
} from "./types";

export class TableWidgetInterface {
    constructor(tableWidget: TableWidget) {
        this.tableWidget = tableWidget;
    }

    isInterfaceActive() {
        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");

        const isOnlySelected =
            canv.canvasController.selected.length === 1 &&
            canv.canvasController.isInSelected(this.tableWidget.parent) &&
            (cursorTool.isMode("") || cursorTool.isMode("interaction"));

        return Boolean(isOnlySelected);
    }

    getAddBarActionsInfo() {
        const result: AddBarActionsInfoResult[] = [];

        if (!this.isInterfaceActive()) return result;
        if (
            !(
                this.tableWidget.interactionType === "" ||
                this.tableWidget.interactionType === "select" ||
                this.tableWidget.interactionType === "cellFocus"
            )
        )
            return result;

        const barPadding = canv.actionManager.getZoomConstant(10);
        const barWidth = canv.actionManager.getZoomConstant(25);

        const firstRowLastCell =
            this.tableWidget.rows[0].cells[this.tableWidget.rows[0].cells.length - 1];

        if (
            isOverRect(
                firstRowLastCell.startX,
                this.tableWidget.startY,
                firstRowLastCell.width + 2 * barPadding + barWidth,
                this.tableWidget.height,
            )
        ) {
            result.push({
                type: "column",
                startX: firstRowLastCell.startX + firstRowLastCell.width + barPadding,
                startY: firstRowLastCell.startY,
                width: barWidth,
                height: this.tableWidget.height,
            });
        }

        const lastRowFirstCell = this.tableWidget.rows[this.tableWidget.rows.length - 1].cells[0];

        if (
            isOverRect(
                this.tableWidget.startX,
                lastRowFirstCell.startY,
                this.tableWidget.width,
                lastRowFirstCell.height + 2 * barPadding + barWidth,
            )
        ) {
            result.push({
                type: "row",
                startX: this.tableWidget.startX,
                startY: lastRowFirstCell.startY + lastRowFirstCell.height + barPadding,
                width: this.tableWidget.width,
                height: barWidth,
            });
        }

        return result;
    }

    isAddBarAction(): AddBarAction {
        const buttons = this.getAddBarActionsInfo();

        for (let i = 0; i < buttons.length; i++) {
            const props = buttons[i];

            if (isOverRect(props.startX, props.startY, props.width, props.height)) {
                return {
                    status: true,
                    data: props,
                };
            }
        }

        return {
            status: false,
        };
    }

    getBuildActionsInfo() {
        const result: ActionsInfoResult[] = [];

        if (!this.isInterfaceActive()) return result;

        this.getBuildRowActionsInfo(result);
        this.getBuildColumnActionsInfo(result);

        return result;
    }

    getBuildRowActionsInfo(result: ActionsInfoResult[]) {
        const zoneSize = canv.actionManager.getZoomConstant(60);

        const catcherHeight = canv.actionManager.getZoomConstant(24);
        const catcherWidth = catcherHeight;
        const catcherWidthMini = canv.actionManager.getZoomConstant(10);

        const adderHalfSize = canv.actionManager.getZoomConstant(12);
        const adderHalfSizeMini = canv.actionManager.getZoomConstant(5);

        for (let i = 0; i < this.tableWidget.rows.length; i++) {
            const firstCell = this.tableWidget.rows[i].cells[0];

            // isOverRowZoneAndNotFly не корректное название
            const isOverRowZoneAndNotFly =
                isOverRect(
                    firstCell.startX - zoneSize,
                    firstCell.startY,
                    zoneSize + this.tableWidget.width,
                    firstCell.height,
                ) &&
                (this.tableWidget.interactionType === "" ||
                    this.tableWidget.interactionType === "select" ||
                    this.tableWidget.interactionType === "cellFocus");

            let isActiveAdder = false;

            if (isOverRowZoneAndNotFly && catcherHeight + 2 * adderHalfSize < firstCell.height) {
                if (i !== 0) {
                    let adderActualHalfSize = adderHalfSizeMini;
                    let adderMode = "light";
                    if (
                        isOverRect(
                            firstCell.startX - zoneSize,
                            firstCell.startY,
                            zoneSize,
                            adderHalfSize,
                        )
                    ) {
                        adderActualHalfSize = adderHalfSize;
                        adderMode = "default";
                        isActiveAdder = true;
                    }

                    result.push({
                        type: "adder",
                        mode: "row",
                        num: i,
                        side: "before",
                        drawData: {
                            startX: firstCell.startX - zoneSize / 2 - adderActualHalfSize,
                            startY: firstCell.startY - adderActualHalfSize,
                            width: 2 * adderActualHalfSize,
                            height: 2 * adderActualHalfSize,
                            levelStartX: firstCell.startX,
                            levelStartY: firstCell.startY,
                            levelWidth: this.tableWidget.width,
                            levelHeight: 0,
                            viewMode: adderMode,
                        },
                    });
                }

                if (i !== this.tableWidget.rows.length - 1) {
                    let adderActualHalfSize = adderHalfSizeMini;
                    let adderMode = "light";
                    if (
                        isOverRect(
                            firstCell.startX - zoneSize,
                            firstCell.startY + firstCell.height - adderHalfSize,
                            zoneSize,
                            adderHalfSize,
                        )
                    ) {
                        adderActualHalfSize = adderHalfSize;
                        adderMode = "default";
                        isActiveAdder = true;
                    }

                    result.push({
                        type: "adder",
                        mode: "row",
                        num: i,
                        side: "after",
                        drawData: {
                            startX: firstCell.startX - zoneSize / 2 - adderActualHalfSize,
                            startY: firstCell.startY + firstCell.height - adderActualHalfSize,
                            width: 2 * adderActualHalfSize,
                            height: 2 * adderActualHalfSize,
                            levelStartX: firstCell.startX,
                            levelStartY: firstCell.startY + firstCell.height,
                            levelWidth: this.tableWidget.width,
                            levelHeight: 0,
                            viewMode: adderMode,
                        },
                    });
                }
            }

            if (catcherHeight < firstCell.height) {
                let exists = isOverRowZoneAndNotFly;
                let catcherActualWidth = catcherWidthMini;
                let catcherMode = "light";

                if (
                    isOverRect(
                        firstCell.startX - zoneSize,
                        firstCell.startY,
                        zoneSize,
                        firstCell.height,
                    ) &&
                    !isActiveAdder
                ) {
                    // эта штука не меняет exists, а только меняет стили
                    // если сюда попали exists уже стоит, так как мы isOverRowZoneAndNotFly (если не fly)
                    catcherActualWidth = catcherWidth;
                    catcherMode = "default";
                }

                if (this.tableWidget.isRowColumnFullSelected().status) {
                    const data = this.tableWidget.isRowColumnFullSelected();

                    if (
                        data.mode === "row" &&
                        i >= data.nums[0] &&
                        i <= data.nums[data.nums.length - 1]
                    ) {
                        catcherActualWidth = catcherWidth;
                        catcherMode = "bright";
                        exists = true;
                    }
                }

                if (exists) {
                    result.push({
                        type: "catcher",
                        mode: "row",
                        num: i,
                        drawData: {
                            startX: firstCell.startX - zoneSize / 2 - catcherActualWidth / 2,
                            startY: firstCell.startY + firstCell.height / 2 - catcherHeight / 2,
                            width: catcherActualWidth,
                            height: catcherHeight,
                            viewMode: catcherMode,
                        },
                    });
                }
            }
        }
    }

    getBuildColumnActionsInfo(result: ActionsInfoResult[]) {
        const zoneSize = canv.actionManager.getZoomConstant(60);

        const catcherWidth = canv.actionManager.getZoomConstant(24);
        const catcherHeight = catcherWidth;
        const cathcerHeightMini = canv.actionManager.getZoomConstant(10);

        const adderHalfSize = canv.actionManager.getZoomConstant(12);
        const adderHalfSizeMini = canv.actionManager.getZoomConstant(5);

        let targetRow = this.tableWidget.rows[0];

        this.tableWidget.rows.map((currRow) => {
            if (currRow.cells.length > targetRow.cells.length) targetRow = currRow;
        });

        const baseStartY = this.tableWidget.rows[0].cells[0].startY;

        for (let i = 0; i < targetRow.cells.length; i++) {
            const firstCell = targetRow.cells[i];

            const isOverColumnZoneAndNotFly =
                isOverRect(
                    firstCell.startX,
                    baseStartY - zoneSize,
                    firstCell.width,
                    this.tableWidget.height + zoneSize,
                ) &&
                (this.tableWidget.interactionType === "" ||
                    this.tableWidget.interactionType === "select" ||
                    this.tableWidget.interactionType === "cellFocus");
            let isActiveAdder = false;

            if (isOverColumnZoneAndNotFly && catcherWidth + 2 * adderHalfSize < firstCell.width) {
                if (i !== 0) {
                    let adderActualHalfSize = adderHalfSizeMini;
                    let adderMode = "light";
                    if (
                        isOverRect(firstCell.startX, baseStartY - zoneSize, adderHalfSize, zoneSize)
                    ) {
                        adderActualHalfSize = adderHalfSize;
                        adderMode = "default";
                        isActiveAdder = true;
                    }

                    result.push({
                        type: "adder",
                        mode: "column",
                        num: i,
                        side: "before",
                        drawData: {
                            startX: firstCell.startX - adderActualHalfSize,
                            startY: baseStartY - zoneSize / 2 - adderActualHalfSize,
                            width: 2 * adderActualHalfSize,
                            height: 2 * adderActualHalfSize,
                            levelStartX: firstCell.startX,
                            levelStartY: baseStartY,
                            levelWidth: 0,
                            levelHeight: this.tableWidget.height,
                            viewMode: adderMode,
                        },
                    });
                }

                if (i !== targetRow.cells.length - 1) {
                    let adderActualHalfSize = adderHalfSizeMini;
                    let adderMode = "light";
                    if (
                        isOverRect(
                            firstCell.startX + firstCell.width - adderHalfSize,
                            baseStartY - zoneSize,
                            adderHalfSize,
                            zoneSize,
                        )
                    ) {
                        adderActualHalfSize = adderHalfSize;
                        adderMode = "default";
                        isActiveAdder = true;
                    }

                    result.push({
                        type: "adder",
                        mode: "column",
                        num: i,
                        side: "after",
                        drawData: {
                            startX: firstCell.startX + firstCell.width - adderActualHalfSize,
                            startY: baseStartY - zoneSize / 2 - adderActualHalfSize,
                            width: 2 * adderActualHalfSize,
                            height: 2 * adderActualHalfSize,
                            levelStartX: firstCell.startX + firstCell.width,
                            levelStartY: baseStartY,
                            levelWidth: 0,
                            levelHeight: this.tableWidget.height,
                            viewMode: adderMode,
                        },
                    });
                }
            }

            if (catcherWidth < firstCell.width) {
                let exists = isOverColumnZoneAndNotFly;
                let catcherActualHeight = cathcerHeightMini;
                let catcherMode = "light";

                if (
                    isOverRect(
                        firstCell.startX,
                        baseStartY - zoneSize,
                        firstCell.width,
                        zoneSize,
                    ) &&
                    !isActiveAdder
                ) {
                    catcherActualHeight = catcherHeight;
                    catcherMode = "default";
                }

                if (this.tableWidget.isRowColumnFullSelected().status) {
                    const data = this.tableWidget.isRowColumnFullSelected();
                    let cellIndex = this.tableWidget.rows[0].getIndexCellByColumnIndex(
                        data.nums[0],
                    );

                    for (let i = 0; i < this.tableWidget.rows.length; i++) {
                        if (
                            this.tableWidget.rows[i].getIndexCellByColumnIndex(data.nums[0]) >
                            cellIndex
                        ) {
                            cellIndex = this.tableWidget.rows[i].getIndexCellByColumnIndex(
                                data.nums[0],
                            );
                        }
                    }

                    if (data.mode === "column" && i === cellIndex) {
                        catcherActualHeight = catcherHeight;
                        catcherMode = "bright";
                        exists = true;
                    }
                }

                if (exists) {
                    const columnIndex = targetRow.cells.reduce((acc, curr, index) => {
                        if (index >= i) return acc;
                        return acc + curr.sectionWidth;
                    }, 0);
                    result.push({
                        type: "catcher",
                        mode: "column",
                        num: i,
                        columnIndex: columnIndex,
                        drawData: {
                            startX: firstCell.startX + firstCell.width / 2 - catcherWidth / 2,
                            startY: baseStartY - zoneSize / 2 - catcherActualHeight / 2,
                            width: catcherWidth,
                            height: catcherActualHeight,
                            viewMode: catcherMode,
                        },
                    });
                }
            }
        }
    }

    isBuildAction(): BuildAction {
        const actions = this.getBuildActionsInfo();
        for (let i = 0; i < actions.length; i++) {
            const action = actions[i];
            if (
                isOverRect(
                    action.drawData.startX,
                    action.drawData.startY,
                    action.drawData.width,
                    action.drawData.height,
                )
            ) {
                return {
                    status: true,
                    data: action,
                };
            }
        }

        return { status: false };
    }

    draw() {
        const addBars = this.getAddBarActionsInfo();

        for (let i = 0; i < addBars.length; i++) {
            this.drawAddBar(
                addBars[i].startX,
                addBars[i].startY,
                addBars[i].width,
                addBars[i].height,
            );
        }

        const buildActions = this.getBuildActionsInfo();
        for (let i = 0; i < buildActions.length; i++) {
            const info = buildActions[i];
            if (info.type === "catcher") {
                this.drawCatcher(
                    info.drawData.startX,
                    info.drawData.startY,
                    info.drawData.width,
                    info.drawData.height,
                    info.drawData.viewMode,
                    info.mode,
                );
            } else {
                this.drawAdder(
                    info.drawData.startX,
                    info.drawData.startY,
                    info.drawData.width,
                    info.drawData.height,
                    info.drawData.viewMode,
                    info.drawData,
                );
            }
        }
    }

    drawCatcher(
        startX: number,
        startY: number,
        width: number,
        height: number,
        drawMode: string,
        mode: string,
    ) {
        // drawModeЖ light, default, bright

        drawRoundedRect(startX, startY, width, height, canv.actionManager.getZoomConstant(5));

        canv.ctx.fillStyle = "#BCE3FF";
        if (drawMode === "bright") {
            canv.ctx.fillStyle = "#0B99FF";
        }
        canv.ctx.fill();

        // Рисуем твиксы ---------------------------------------------

        if (drawMode === "default" || drawMode === "bright") {
            canv.ctx.fillStyle = "#0B99FF";
            if (drawMode === "bright") {
                canv.ctx.fillStyle = "#FFFFFF";
            }

            if (mode === "row") {
                const twixWidth = width / 10;
                const twixHeight = height / 2;

                drawRoundedRect(
                    startX + width / 2 - (twixWidth * 3) / 2,
                    startY + height / 2 - twixHeight / 2,
                    twixWidth,
                    twixHeight,
                    canv.actionManager.getZoomConstant(1),
                );
                canv.ctx.fill();
                drawRoundedRect(
                    startX + width / 2 + twixWidth / 2,
                    startY + height / 2 - twixHeight / 2,
                    twixWidth,
                    twixHeight,
                    canv.actionManager.getZoomConstant(1),
                );
                canv.ctx.fill();
            } else {
                const twixWidth = width / 2;
                const twixHeight = height / 10;

                drawRoundedRect(
                    startX + width / 2 - twixWidth / 2,
                    startY + height / 2 - (twixHeight * 3) / 2,
                    twixWidth,
                    twixHeight,
                    canv.actionManager.getZoomConstant(1),
                );
                canv.ctx.fill();
                drawRoundedRect(
                    startX + width / 2 - twixWidth / 2,
                    startY + height / 2 + twixHeight / 2,
                    twixWidth,
                    twixHeight,
                    canv.actionManager.getZoomConstant(1),
                );
                canv.ctx.fill();
            }
        }
    }

    drawAdder(
        startX: number,
        startY: number,
        width: number,
        height: number,
        drawMode: string,
        allData: any,
    ) {
        // drawModeЖ light, default

        // единственный способ получить тут bright
        if (drawMode === "default" && isOverRect(startX, startY, width, height)) {
            drawMode = "bright";
        }

        // рисуем круг -------------------

        canv.ctx.fillStyle = "#BCE3FF";
        if (drawMode === "bright") {
            canv.ctx.fillStyle = "#0B99FF";
        }

        drawRoundedRect(startX, startY, width, height, canv.actionManager.getZoomConstant(100));
        canv.ctx.fill();

        // Рисуем плюсик

        if (drawMode === "default" || drawMode === "bright") {
            const plusCenterX = startX + width / 2;
            const plusCenterY = startY + width / 2;
            const plusWidth = width / 10;
            const plusHeight = height / 2;

            canv.ctx.fillStyle = "#0B99FF";
            if (drawMode === "bright") {
                // выделяяем разделитель строки
                drawRoundedRect(
                    allData.levelStartX,
                    allData.levelStartY,
                    allData.levelWidth,
                    allData.levelHeight,
                    0,
                );
                canv.ctx.strokeStyle = "#0B99FF";
                canv.ctx.lineWidth = canv.actionManager.getZoomConstant(4);
                canv.ctx.stroke();
                // выбираем цвет для отрисовки плюсика
                canv.ctx.fillStyle = "#FFFFFF";
            }

            drawRoundedRect(
                plusCenterX - plusWidth / 2,
                plusCenterY - plusHeight / 2,
                plusWidth,
                plusHeight,
                canv.actionManager.getZoomConstant(1),
            );
            canv.ctx.fill();
            drawRoundedRect(
                plusCenterX - plusHeight / 2,
                plusCenterY - plusWidth / 2,
                plusHeight,
                plusWidth,
                canv.actionManager.getZoomConstant(1),
            );
            canv.ctx.fill();
        }
    }

    drawAddBar(startX: number, startY: number, width: number, height: number) {
        drawRoundedRect(startX, startY, width, height, canv.actionManager.getZoomConstant(5));

        canv.ctx.fillStyle = "#BCE3FF";
        if (isOverRect(startX, startY, width, height)) {
            canv.ctx.fillStyle = "#0B99FF";
        }

        canv.ctx.fill();

        const minDim = width < height ? width * 0.7 : height * 0.7;
        const plusLineWidth = minDim * 0.1;
        const plusLineHeight = minDim * 0.6;

        canv.ctx.fillStyle = "#0B99FF";
        if (isOverRect(startX, startY, width, height)) {
            canv.ctx.fillStyle = "#FFFFFF";
        }

        drawRoundedRect(
            startX + width / 2 - plusLineHeight / 2,
            startY + height / 2 - plusLineWidth / 2,
            plusLineHeight,
            plusLineWidth,
            0,
        );
        canv.ctx.fill();
        drawRoundedRect(
            startX + width / 2 - plusLineWidth / 2,
            startY + height / 2 - plusLineHeight / 2,
            plusLineWidth,
            plusLineHeight,
            0,
        );
        canv.ctx.fill();
    }

    tableWidget: TableWidget;
}
